'use client';
import { useEffect, useRef } from 'react';
import { clsx } from 'clsx';
import { Dialog } from 'radix-ui';
import { useEpgProgress } from 'hooks/useEpgProgress';
import type { PropsWithClassName } from 'types/react-props';
import { Icon } from '../Icon';
import DetailView from './components/DetailView';
import styles from './EpgItem.module.scss';

export type EpgItemProps = PropsWithClassName<{
  readonly id: string;
  readonly startDate: string;
  readonly endDate: string;
  readonly name: string;
  readonly description?: string | null;
  readonly subtitle?: string | null;
  readonly image: string | null;
  readonly link?: string | null;
  readonly rtlPlusLink?: string | null;
  readonly hasUT?: boolean;
}>;

export const EpgItem = ({
  id,
  startDate,
  endDate,
  name,
  subtitle,
  description,
  image,
  link,
  rtlPlusLink,
  hasUT,
  className,
}: EpgItemProps) => {
  const detailViewRef = useRef<HTMLDivElement | null>(null);
  const { isPlaying } = useEpgProgress(startDate, endDate);
  const wasPlayingOnMountRef = useRef<boolean>(isPlaying);

  const startDateLabel = new Intl.DateTimeFormat('de-DE', {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(new Date(startDate));

  useEffect(() => {
    if (wasPlayingOnMountRef.current && detailViewRef.current) {
      const element = detailViewRef.current;
      const offset = 160;
      const top = element.getBoundingClientRect().top + window.scrollY - offset;

      window.scrollTo({
        top,
        behavior: 'smooth',
      });
    }
  }, [isPlaying]);

  if (isPlaying) {
    return (
      <DetailView
        ref={detailViewRef}
        id={id}
        startDate={startDate}
        endDate={endDate}
        name={name}
        subtitle={subtitle}
        description={description}
        image={image}
        link={link}
        rtlPlusLink={rtlPlusLink}
        hasUT={hasUT}
      />
    );
  }

  return (
    <Dialog.Root>
      <Dialog.Trigger className={clsx(styles.item, className)}>
        <div className={styles.time}>{startDateLabel}</div>
        <div>
          <span className={styles.title}>{name}</span>
          {(!!subtitle || hasUT) && (
            <span className={styles.subtitle}>
              {hasUT && <span className={styles.ut}>UT</span>}
              {subtitle}
            </span>
          )}
        </div>
      </Dialog.Trigger>

      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay}>
          <Dialog.Content className={styles.detailContent} aria-describedby={undefined}>
            <Dialog.Close asChild>
              <button className={styles.closeButton} aria-label="Schließen">
                <Icon name="close" size={16} />
              </button>
            </Dialog.Close>
            <DetailView
              className={styles.detailView}
              id={id}
              startDate={startDate}
              endDate={endDate}
              name={name}
              subtitle={subtitle}
              description={description}
              image={image}
              link={link}
              rtlPlusLink={rtlPlusLink}
              hasUT={hasUT}
              isDialog
            />
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
